import { Link, useForm } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatCurrency } from '@/lib/utils'
import { ArrowLeft, CheckCircle2, Receipt, ArrowRight } from 'lucide-react'
import { format } from 'date-fns'

type Props = {
  title: string
  breadcrumbs: { label: string; href?: string }[]
  backUrl: string
  invoice: {
    id: string
    number: string
    issued_at: string
    due_date: string | null
    notes: string | null
    line_items_snapshot: { description: string; amount_cents: number }[]
  }
  payment_status: 'unpaid' | 'paid' | 'refunded'
  receipt?: {
    number: string
    issued_at: string
    amount_cents: number
    payment_method: string
  }
  total_cents: number
  updatePath: string
  markPaidPath: string
  newRefundPath: string
}

export default function InvoiceShow({
  breadcrumbs, backUrl, invoice, payment_status, receipt, total_cents,
  updatePath, markPaidPath, newRefundPath
}: Props) {
  const { data, setData, put, processing: updating } = useForm({
    due_date: invoice.due_date || '',
    notes: invoice.notes || ''
  })

  const { post, processing: markingPaid } = useForm({})

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    put(updatePath)
  }

  const handleMarkPaid = () => {
    if (confirm("Are you sure you want to mark this invoice as paid? This will generate a receipt immediately.")) {
      post(markPaidPath)
    }
  }

  return (
    <DashboardLayout title={`Invoice ${invoice.number}`} breadcrumbs={breadcrumbs}>
      <div className="max-w-3xl space-y-6 flex gap-6 items-start">
        
        {/* Main Content */}
        <div className="flex-1 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" asChild>
                <Link href={backUrl}><ArrowLeft className="w-4 h-4" /></Link>
              </Button>
              <h1 className="text-2xl font-bold tracking-tight">Invoice {invoice.number}</h1>
            </div>
            
            <div className="flex gap-2">
              {payment_status === 'unpaid' && (
                <Button onClick={handleMarkPaid} disabled={markingPaid}>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Mark as Paid
                </Button>
              )}
            </div>
          </div>

          <div className="bg-card border rounded-lg p-6">
            <h2 className="font-semibold mb-4 text-lg">Line Items</h2>
            <div className="space-y-3">
              {invoice.line_items_snapshot.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                  <span className="text-sm font-medium">{item.description}</span>
                  <span className="text-sm text-muted-foreground">{formatCurrency(item.amount_cents)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-4 font-bold text-lg">
                <span>Total</span>
                <span>{formatCurrency(total_cents)}</span>
              </div>
            </div>
          </div>

          {payment_status === 'unpaid' && (
            <form onSubmit={handleUpdate} className="bg-card border rounded-lg p-6 space-y-4">
              <h2 className="font-semibold mb-2">Edit Invoice Details</h2>
              
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={data.due_date}
                  onChange={(e) => setData('due_date', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  rows={4}
                  value={data.notes}
                  onChange={(e) => setData('notes', e.target.value)}
                  placeholder="Optional notes to display on the invoice..."
                />
              </div>

              <Button type="submit" disabled={updating}>
                Save Changes
              </Button>
            </form>
          )}

          {payment_status === 'paid' && receipt && (
            <div className="bg-green-50/50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="font-semibold text-green-700 dark:text-green-400 flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-5 h-5" />
                    Payment Received
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Receipt <strong>{receipt.number}</strong> issued on {format(new Date(receipt.issued_at), 'PPP')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Method: <span className="uppercase">{receipt.payment_method}</span>
                  </p>
                </div>
                <Button variant="outline" asChild>
                  <Link href={newRefundPath}>
                    Issue Refund
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          )}

          {payment_status === 'refunded' && (
            <div className="bg-orange-50/50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900 rounded-lg p-6">
              <h2 className="font-semibold text-orange-700 dark:text-orange-400 mb-1 flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Payment Refunded
              </h2>
              <p className="text-sm text-muted-foreground">
                This payment has been fully or partially refunded. Check the refund record for details.
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
